import React, { useState, useEffect, useRef } from 'react';
import { FaCalendarTimes, FaDownload, FaPrint, FaCheckCircle, FaWpforms } from 'react-icons/fa';
import { Button, ButtonGroup, Col, Dropdown, Row, Modal, Form } from 'react-bootstrap';
import { MdAccessTime, MdArrowForward, MdArrowBack } from 'react-icons/md';
import { TbReplaceFilled } from 'react-icons/tb';
import { IoEye } from 'react-icons/io5';
import { TiDocumentAdd } from 'react-icons/ti'; // Fixed import
import 'react-datepicker/dist/react-datepicker.css';
import { Table } from 'react-bootstrap';
import AtdLayout from '../../components/AttendanceLayout'; // Adjust path as needed
import axios from '../../Authservices/axiosInstance';
export const ViewAbsDay = () => {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [modalData, setModalData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentAbsence, setCurrentAbsence] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [attendanceId, setAttendanceId] = useState(null);
  const [selectedDocUrl, setSelectedDocUrl] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalData, setApprovalData] = useState({});
  const [showDeductionColumn, setShowDeductionColumn] = useState(false); // New state for deduction column
  const fileInputRef = useRef(null);
  const printRef = useRef();

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchAbsentees = async (date) => {
    setLoading(true);
    setError(null);
    try {
      const formattedDate = formatDateForAPI(date);
      const response = await axios.get(
          `https://backend.ems.uk.hiradentallab.com/api/attendance/absent-users/${formattedDate}`
      );
      if (response.data.success) {
        const formattedAbsences = response.data.absents.map((item) => ({
          _id: item._id,
          userId: item.userId,
          name: item.name,
          department: item.department || 'Not specified',
          date: item.date,
          document: item.document || null,
          reason: item.reason || 'Not specified',
          approved: item.approved || false,
          markedBy: item.markedBy || 'System',
          markedAt: item.markedAt || new Date().toISOString(),
          deductionMultiplier: item.deductionMultiplier || 1, // Include deductionMultiplier
        }));
        setAbsences(formattedAbsences);
        // Check if date is Monday (1) or Saturday (6)
        const day = date.getDay();
        setShowDeductionColumn(day === 1 || day === 6);
      } else {
        setError(response.data.message || 'Failed to fetch absentees');
        setAbsences([]);
        setShowDeductionColumn(false);
      }
    } catch (err) {
      console.error('Error fetching absentees:', err);
      setError(err.response?.data?.message || 'Failed to fetch absentees');
      setAbsences([]);
      setShowDeductionColumn(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAbsentees(selectedDate);
  }, [selectedDate]);

  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  const handleDateChange = (direction) => {
    setSelectedDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(direction === 'forward' ? prevDate.getDate() + 1 : prevDate.getDate() - 1);
      return newDate;
    });
  };

  const handleShowModal = (item) => {
    setModalData(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile || !currentAbsence) return;

    try {
      setError(null);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('document', selectedFile);
      formData.append('userId', currentAbsence.userId);
      formData.append('name', currentAbsence.name);
      formData.append('department', currentAbsence.department);
      formData.append('date', formatDateForAPI(selectedDate));

      const endpoint = currentAbsence._id
          ? `https://backend.ems.uk.hiradentallab.com/api/attendance/upload-document/${currentAbsence._id}`
          : 'https://backend.ems.uk.hiradentallab.com/api/attendance/upload-document';

      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      if (response.data.success) {
        setAbsences((prev) =>
            prev.map((item) =>
                item.userId === currentAbsence.userId && item.date === currentAbsence.date
                    ? {
                      ...item,
                      document: response.data.documentUrl,
                      _id: response.data.data._id,
                      updatedAt: new Date().toISOString(),
                    }
                    : item
            )
        );
        handleCloseUploadModal();
      } else {
        setError(response.data.message || 'Failed to upload document');
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to upload document');
    }
  };

  const handleViewDocument = (docUrl, item) => {
    if (!docUrl || !item || !item._id) {
      console.warn('CHECK FAILED');
      console.error('Invalid document or item:', { docUrl, item });
      alert('Something went wrong. Document or attendance data missing.');
      return;
    }

    let formattedUrl = docUrl.startsWith('http')
        ? docUrl
        : `https://backend.ems.uk.hiradentallab.com${docUrl.startsWith('/uploads') ? docUrl : `/uploads/${docUrl}`}`;

    setSelectedDocUrl(formattedUrl);
    setShowViewModal(true);
    setAttendanceId(item._id);
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(selectedDocUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = selectedDocUrl.split('/').pop() || 'document';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  const handleFileReplace = async (e) => {
    const file = e.target.files[0];
    if (!file || !attendanceId) {
      alert('No file selected or attendance ID missing.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('document', file);

      const response = await axios.post(
          `https://backend.ems.uk.hiradentallab.com/api/attendance/replace-document/${attendanceId}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
      );

      if (response.data.success) {
        setAbsences((prev) =>
            prev.map((item) =>
                item._id === attendanceId
                    ? {
                      ...item,
                      document: response.data.documentUrl,
                      updatedAt: new Date().toISOString(),
                    }
                    : item
            )
        );
        setSelectedDocUrl(
            response.data.documentUrl.startsWith('http')
                ? response.data.documentUrl
                : `https://backend.ems.uk.hiradentallab.com${response.data.documentUrl}`
        );
        alert('Document replaced successfully!');
      } else {
        throw new Error(response.data.message || 'Failed to replace document');
      }
    } catch (error) {
      console.error('Error replacing file:', error);
      alert(error.message || 'Error replacing file.');
    }
  };

  const handleOpenApprovalForm = (item) => {
    setApprovalData({
      userName: item.name,
      department: item.department,
      date: new Date(item.date).toLocaleDateString(),
      reason: item.reason || 'Not specified',
      deductionMultiplier: item.deductionMultiplier || 1, // Include deductionMultiplier
    });
    setShowApprovalModal(true);
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  return (
      <AtdLayout>
        <div>
          <h1 className='yesterday-attendance-title'>
            <FaCalendarTimes /> View Absences
          </h1>
          <hr />
          <div className='available-leaves-content'>
            <div className='available-leaves-actions'>
              <Row>
                <Col xs={12} md={6} className='d-flex align-items-center mb-2'>
                  <Dropdown as={ButtonGroup}>
                    <Button variant='primary'>Show Entries</Button>
                    <Dropdown.Toggle split variant='primary' id='dropdown-split-basic' />
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => setRowsPerPage(10)}>10</Dropdown.Item>
                      <Dropdown.Item onClick={() => setRowsPerPage(25)}>25</Dropdown.Item>
                      <Dropdown.Item onClick={() => setRowsPerPage(50)}>50</Dropdown.Item>
                      <Dropdown.Item onClick={() => setRowsPerPage(100)}>100</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
                <Col xs={12} md={6} className='d-flex justify-content-end mb-2'>
                  <div
                      style={{
                        backgroundColor: '#fff',
                        width: '250px',
                        textAlign: 'center',
                        borderRadius: '8px',
                        padding: '8px',
                      }}
                  >
                    <button className='date-nav-btn' onClick={() => handleDateChange('backward')}>
                      <MdArrowBack />
                    </button>
                    <span className='date-display'>{selectedDate.toDateString()}</span>
                    <button
                        className='date-nav-btn'
                        onClick={() => handleDateChange('forward')}
                        disabled={isFutureDate(selectedDate)}
                        style={{
                          opacity: isFutureDate(selectedDate) ? 0.5 : 1,
                          cursor: isFutureDate(selectedDate) ? 'not-allowed' : 'pointer',
                        }}
                    >
                      <MdArrowForward />
                    </button>
                  </div>
                </Col>
              </Row>
            </div>
            {error && (
                <div className='alert alert-danger' role='alert'>
                  {error}
                </div>
            )}
            <div className='table-responsive'>
              {loading ? (
                  <div className='text-center my-4'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    <p className='mt-2'>Loading absentees data...</p>
                  </div>
              ) : (
                  <Table striped hover className='table-style'>
                    <thead className='yesterday-attendance-table-header'>
                    <tr className='table-header'>
                      <th>SR</th>
                      <th>User ID</th>
                      <th>Employee Name</th>
                      <th>Date</th>
                      <th>Department</th>
                      {showDeductionColumn && <th>Deduction</th>}
                      <th>Document</th>
                      <th>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    {absences.length > 0 ? (
                        absences
                            .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                            .map((item, index) => (
                                <tr key={index}>
                                  <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                                  <td>{item.userId}</td>
                                  <td>{item.name}</td>
                                  <td>{new Date(item.date).toLocaleDateString()}</td>
                                  <td>{item.department}</td>
                                  {showDeductionColumn && <td>{item.deductionMultiplier}x</td>}
                                  <td>
                                    {item.document ? (
                                        <>
                                          <Button
                                              style={{
                                                color: '#fff',
                                                textDecoration: 'none',
                                                fontWeight: '500',
                                                padding: '4px 8px',
                                                backgroundColor: '#17a2b8',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                marginLeft: '9px',
                                                border: 'none',
                                              }}
                                              onClick={() => handleViewDocument(item.document, item)}
                                          >
                                            <IoEye />
                                          </Button>
                                          <Button
                                              variant='success'
                                              size='sm'
                                              style={{ marginLeft: '5px' }}
                                              onClick={() => handleOpenApprovalForm(item)}
                                          >
                                            <FaCheckCircle />
                                          </Button>
                                        </>
                                    ) : (
                                        <>
                                          <Button
                                              variant='primary'
                                              size='sm'
                                              onClick={() => {
                                                setCurrentAbsence(item);
                                                setShowUploadModal(true);
                                              }}
                                          >
                                            <TiDocumentAdd />
                                          </Button>
                                          <Button
                                              variant='danger'
                                              size='sm'
                                              style={{ marginLeft: '5px' }}
                                              onClick={() => handleOpenApprovalForm(item)}
                                          >
                                            <FaWpforms />
                                          </Button>
                                        </>
                                    )}
                                  </td>
                                  <td>
                                    <Button variant='success' size='sm' onClick={() => handleShowModal(item)}>
                                      <MdAccessTime />
                                    </Button>
                                  </td>
                                </tr>
                            ))
                    ) : (
                        <tr>
                          <td colSpan={showDeductionColumn ? 8 : 7} className='text-center'>
                            {error ? 'Error loading data' : 'No absentees found for this date'}
                          </td>
                        </tr>
                    )}
                    </tbody>
                    <tfoot className='provident-fund-footer'>
                    <tr className='provident-fund-table-footer'>
                      <td>Total Absences</td>
                      <td>{absences.length}</td>
                      <td colSpan={showDeductionColumn ? 6 : 5}></td>
                    </tr>
                    </tfoot>
                  </Table>
              )}
            </div>

            {/* Absence Details Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered size='lg' dialogClassName='fancy-modal'>
              <Modal.Header closeButton>
                <Modal.Title>Absence Details</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p><strong>User ID:</strong> {modalData.userId}</p>
                <p><strong>Name:</strong> {modalData.name}</p>
                <p><strong>Department:</strong> {modalData.department || 'Not specified'}</p>
                <p><strong>Date:</strong> {modalData.date && new Date(modalData.date).toDateString()}</p>
                {modalData.deductionMultiplier > 1 && (
                    <p><strong>Deduction:</strong> {modalData.deductionMultiplier}x</p>
                )}
                <p><strong>Reason:</strong> {modalData.reason || 'Not specified'}</p>
                <p><strong>Status:</strong> {modalData.approved ? 'Approved' : 'Pending'}</p>
                <p><strong>Recorded By:</strong> {modalData.markedBy || 'System'}</p>
                <p><strong>Recorded At:</strong> {modalData.markedAt && new Date(modalData.markedAt).toLocaleString()}</p>
                <p><strong>Document:</strong> {modalData.document ? 'Attached' : 'No document'}</p>
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={handleCloseModal}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Document Upload Modal */}
            <Modal show={showUploadModal} onHide={handleCloseUploadModal} centered>
              <Modal.Header closeButton>
                <Modal.Title>Upload Document</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group controlId='formFile' className='mb-3'>
                    <Form.Label>Select document to upload</Form.Label>
                    <Form.Control
                        type='file'
                        onChange={handleFileSelect}
                        accept='.pdf,.doc,.docx,.jpg,.png,.jpeg'
                    />
                    <Form.Text className='text-muted'>
                      Supported formats: PDF, DOC, DOCX, JPG, PNG, JPEG
                    </Form.Text>
                  </Form.Group>
                  {selectedFile && (
                      <div className='mb-3'>
                        <p>Selected file: {selectedFile.name}</p>
                        <p>File size: {(selectedFile.size / 1024).toFixed(2)} KB</p>
                      </div>
                  )}
                  {uploadProgress > 0 && (
                      <div className='mb-3'>
                        <div className='progress'>
                          <div
                              className='progress-bar progress-bar-striped progress-bar-animated'
                              role='progressbar'
                              style={{ width: `${uploadProgress}%` }}
                              aria-valuenow={uploadProgress}
                              aria-valuemin='0'
                              aria-valuemax='100'
                          >
                            {uploadProgress}%
                          </div>
                        </div>
                      </div>
                  )}
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={handleCloseUploadModal}>
                  Cancel
                </Button>
                <Button
                    variant='primary'
                    onClick={handleUpload}
                    disabled={!selectedFile || uploadProgress > 0}
                >
                  Upload
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Document View Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size='lg'>
              <Modal.Header closeButton>
                <Modal.Title
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                >
                  <span>View Document</span>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {selectedDocUrl && (
                        <Button
                            variant='outline-success'
                            style={{
                              borderRadius: '10px',
                              padding: '6px 16px',
                              fontWeight: 'bold',
                              background: 'linear-gradient(to right, #6a11cb, #2575fc)',
                              color: '#fff',
                              border: 'none',
                              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                              fontSize: '1rem',
                            }}
                            onClick={handleDownload}
                        >
                          <FaDownload />
                        </Button>
                    )}
                    <Button
                        variant='outline-warning'
                        style={{
                          borderRadius: '10px',
                          padding: '6px 16px',
                          fontWeight: 'bold',
                          background: 'linear-gradient(to right, #6a11cb, #2575fc)',
                          color: '#fff',
                          border: 'none',
                          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                          fontSize: '1rem',
                          marginRight: '40px',
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                      <TbReplaceFilled />
                    </Button>
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='.jpg,.png,.pdf'
                        style={{ display: 'none' }}
                        onChange={handleFileReplace}
                    />
                  </div>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ textAlign: 'center' }}>
                {selectedDocUrl ? (
                    selectedDocUrl.endsWith('.pdf') ? (
                        <iframe
                            src={selectedDocUrl}
                            width='100%'
                            height='500px'
                            style={{ border: 'none' }}
                            title='PDF Document Viewer'
                        ></iframe>
                    ) : (
                        <img
                            src={selectedDocUrl}
                            alt='Uploaded Document'
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              borderRadius: '8px',
                            }}
                            onError={(e) => {
                              e.target.src = '/fallback-image.png';
                            }}
                        />
                    )
                ) : (
                    <p style={{ color: 'red', fontWeight: 'bold' }}>
                      No document available
                    </p>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={() => setShowViewModal(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Approval Form Modal */}
            <Modal show={showApprovalModal} onHide={() => setShowApprovalModal(false)} size='lg'>
              <Modal.Header closeButton>
                <Modal.Title>Absent Approval Form</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div
                    ref={printRef}
                    className='approval-form'
                    style={{ padding: '30px', fontFamily: 'Segoe UI, sans-serif', backgroundColor: '#fff' }}
                >
                  <h3 className='text-center mb-4'>Absent Approval Form</h3>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '30px' }}>
                    <div style={{ flex: 1 }}>
                      <p><strong>Date:</strong> {approvalData.date}</p>
                      <p><strong>Employee Name:</strong> {approvalData.userName}</p>
                      <p><strong>Department:</strong> {approvalData.department}</p>
                      <p><strong>Reason:</strong> {approvalData.reason}</p>
                      {approvalData.deductionMultiplier > 1 && (
                          <p><strong>Deduction:</strong> {approvalData.deductionMultiplier}x</p>
                      )}
                    </div>
                    <div
                        style={{
                          flexShrink: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '20px',
                          justifyContent: 'flex-start',
                          marginTop: '10px',
                        }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div
                            style={{
                              width: '20px',
                              height: '20px',
                              border: '2px solid #000',
                            }}
                        ></div>
                        <span><strong>Approved</strong></span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div
                            style={{
                              width: '20px',
                              height: '20px',
                              border: '2px solid #000',
                            }}
                        ></div>
                        <span><strong>Rejected</strong></span>
                      </div>
                    </div>
                  </div>
                  <div style={{ borderBottom: '2px solid #000', width: '100%', margin: '30px 0' }}></div>
                  <div style={{ borderBottom: '2px solid #000', width: '100%', margin: '30px 0' }}></div>
                  <div className='signature-area mt-4'>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      {['Department Leader', 'HR', 'Managing Director'].map((role) => (
                          <div key={role} style={{ textAlign: 'center', width: '30%' }}>
                            <p><strong>{role}</strong></p>
                            <div
                                style={{
                                  borderBottom: '1px solid #000',
                                  width: '100%',
                                  margin: '30px auto',
                                }}
                            ></div>
                          </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={() => setShowApprovalModal(false)}>
                  Close
                </Button>
                <Button variant='primary' onClick={handlePrint}>
                  <FaPrint /> Print
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </div>
      </AtdLayout>
  );
};

export default ViewAbsDay;